import React, { useState } from 'react';
import { Input, Label } from 'reactstrap';

import { formatSettingLabel } from '~libs/stringarly';

import Button from '~components/Button';
import Icon, { faCheck, faSave } from '~components/icon';

import { useGetSettingsQuery } from '~lookups/actions/settings';
import {
  useCreateCompanySettingMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingMutation,
} from '~main/actions/companySettings';

interface ComponentProps {
  isActive: boolean;
}

const feature_tag = 'supplierinvoices';

const CompanySettings: React.FC<ComponentProps> = ({ isActive }) => {
  const { data: definitions = [] } = useGetSettingsQuery({ feature_tag }, { skip: !isActive });
  const { data: settings = [] } = useGetCompanySettingsQuery(undefined, { skip: !isActive });
  const [createSetting] = useCreateCompanySettingMutation();
  const [updateSetting] = useUpdateCompanySettingMutation();

  const [values, setValues] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const handleChange = (key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (lookup_setting_id: string, value: string, isNew: boolean) => {
    setSavingId(lookup_setting_id);
    try {
      if (isNew) await createSetting({ lookup_setting_id, value });
      else {
        const setting = settings.find((setting) => setting.setting.id === lookup_setting_id);
        if (setting) await updateSetting({ id: setting.id, value });
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="company-settings-form">
      {definitions.map((definition) => {
        const { id, key } = definition;
        const setting = settings.find((setting) => setting.setting.id === id);
        const existingValue = setting?.value ?? '';
        const value = values[key] ?? existingValue;
        const isNew = !setting;
        const isDirty = isNew || value !== existingValue;

        return (
          <div key={key} className="setting-row">
            <Label for={key}>{formatSettingLabel(key)}</Label>
            <Input id={key} value={value} onChange={({ target }) => handleChange(key, target.value)} />
            <Button
              onClick={() => handleSave(id, value, isNew)}
              disabled={savingId === id || !isDirty}
              color={isDirty ? 'success' : 'secondary'}
              title={isDirty ? 'Save changes' : 'No changes to save'}
            >
              <Icon icon={isDirty ? faSave : faCheck} size="lg" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

export default CompanySettings;
